import classNames from 'classnames';
import dayjs from 'dayjs';
import { formatCurrency } from 'lib/helpers/strings';
import Member from 'modules/App/data-components/Member';
import { AvailableProfile, User } from 'modules/App/interfaces';
import Modal from 'modules/common/components/Modal';
import Areas from 'modules/geo/components/Areas';
import CountryEmojis from 'modules/geo/data-components/CountryEmojis';
import { Link } from 'modules/i18n';
import { useRouter } from 'next/router';
import Tooltip from 'rc-tooltip';
import React from 'react';
import { MdAssignment as IconBountyStart } from 'react-icons/md';
import HowDoesItWork from './AvailableProfileHowDoesItWork';
import HeadHunterAvatar from './HeadHunterAvatar';

export type AvailableProfileDetailsProps = {
  availableProfile: AvailableProfile;
  withTitle?: boolean;
  withFees?: boolean;
  mySearch?: boolean;
  openDescription?: boolean;
  onProposeMission?: (data: { showHeadhunter?: boolean }) => Promise<void>;
  headhunterId?: string;
} & React.HTMLAttributes<HTMLDListElement>;

const experiences = {
  '<2': '0 à 2 ans',
  '2/4': '2 à 4 ans',
  '3/5': '3 à 5 ans',
  '5/8': '5 à 8 ans',
  '8/10': '8 à 10 ans',
  '>10': '> 10 ans',
};

const AvailableProfileDetails: React.FC<AvailableProfileDetailsProps> = ({
  children,
  withTitle = false,
  withFees = false,
  openDescription = false,
  mySearch = false,
  className,
  availableProfile,
  onProposeMission,
  headhunterId,
  ...props
}) => {
  const [memberId, toggleMember] = React.useState<string | undefined>();

  const availableProfileCreatedBy = availableProfile?.createdBy as User;
  const router = useRouter();
  const {
    name,
    sector,
    occupation,
    client,
    experience,
    baseSalary,
    bountyDescription,
    bounty,
    platformFees,
    description,
    context,
    publishedAt,
    createdAt,
    areaSearch,
    countryIds,
    currency,
  } = availableProfile;

  return (
    <>
      <Modal onClose={() => toggleMember(undefined)} open={!!memberId} title={'Détail'} className="p-2 max-w-screen-lg">
        {memberId && (
          <div className="text-left w-full p-2 md:p-8">
            <Member className="text-md md:text-lg" _id={memberId} openDescription={true} />
          </div>
        )}
      </Modal>

      <main className={classNames('min-h-full', className)} {...props}>
        <div
          className={classNames(
            'max-w-3xl mx-auto px-4 sm:px-6 lg:flex lg:items-start lg:justify-between lg:space-x-5 lg:max-w-7xl',
            withTitle ? ' lg:px-8' : ''
          )}
        >
          {withTitle && (
            <div className="sm:flex items-start sm:space-x-5">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 pl-0 pb-0 mb-0">{name}</h1>
                <p className="text-xs font-medium text-gray-500 mt-2 italic flex items-center gap-1">
                  Proposé par <HeadHunterAvatar user={availableProfileCreatedBy} withCompany={false} />
                </p>
                <p className="text-xs font-medium text-gray-500 mt-2 italic flex items-center gap-1">
                  le{' '}
                  <time className="text-gray-900" dateTime={dayjs(createdAt).format()}>
                    {dayjs(createdAt).format('LL')}
                  </time>
                </p>
                {!publishedAt && (
                  <div>
                    <span className="badge badge-error badge-xs">Dépublié</span>
                  </div>
                )}
              </div>
            </div>
          )}
          {withFees && (
            <Tooltip
              placement="bottom"
              trigger={['click', 'hover']}
              overlayClassName="opacity-100"
              overlay={
                <>
                  <h2 className="font-bold text-sm">Conditions de {availableProfileCreatedBy?.name} pour l’obtention de sa prime</h2>
                  <p className="mt-1 text-sm text-gray-900">{bountyDescription}</p>
                </>
              }
            >
              <div className="stats shadow">
                <div className="stat">
                  <div className="stat-figure text-primary">
                    <IconBountyStart />
                  </div>
                  <div className="stat-title">Prime souhaitée</div>
                  <div className="stat-value text-2xl text-primary">{formatCurrency({ locale: router.locale, value: bounty, currency })} HT</div>
                </div>
              </div>
            </Tooltip>
          )}
        </div>

        <div className={classNames('max-w-3xl mx-auto grid grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl', withTitle || withFees ? 'mt-8' : '')}>
          <div className="space-y-6 lg:col-start-1 lg:col-span-2">
            {/* Description list*/}
            <section aria-labelledby="mission-information-title" className="p-0">
              <div className="bg-white shadow sm:rounded-lg">
                <div className="px-4 py-2 sm:px-6 md:flex md:justify-between md:items-center">
                  <h2 id="mission-information-title" className="text-lg leading-6 font-medium text-gray-900">
                    Le candidat
                  </h2>
                  <div className="flex items-end flex-wrap gap-2">
                    {onProposeMission && (
                      <button onClick={() => onProposeMission({ showHeadhunter: true })} className="btn btn-primary btn-outline" disabled={mySearch}>
                        {mySearch ? 'Tu es le consultant' : 'Contacte le consultant'}
                      </button>
                    )}
                    {onProposeMission && (
                      <button onClick={() => onProposeMission({})} type="button" className="btn btn-primary" disabled={mySearch}>
                        Propose une mission
                      </button>
                    )}
                    {mySearch && (
                      <Link href={`/contacts/mission/received?q=${encodeURIComponent(name)}`} className="btn btn-primary">
                        Missions reçues
                      </Link>
                    )}
                  </div>
                </div>
                <Tooltip
                  placement="top"
                  trigger={['click', 'hover']}
                  overlayClassName="opacity-100"
                  overlay={<HowDoesItWork bounty={bounty} platformFees={platformFees} mySearch={mySearch} />}
                >
                  <div className="mx-4 my-2 text-right text-gray-600 text-xs cursor-zoom-out">ℹ️ Comment ça marche ?</div>
                </Tooltip>

                <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
                  <dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
                    {areaSearch && (
                      <div className="sm:col-span-1">
                        <dt className="text-sm font-medium text-gray-500">Zone géographique</dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          <CountryEmojis countryIds={countryIds || []} />
                          <Areas items={areaSearch?.areas || []} />
                        </dd>
                      </div>
                    )}
                    <div className="sm:col-span-1">
                      <dt className="text-sm font-medium text-gray-500">Salaire</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {formatCurrency({ locale: router.locale, value: +baseSalary?.min, compact: true, currency })} à{' '}
                        {formatCurrency({ locale: router.locale, value: +baseSalary?.max, compact: true, currency })}
                      </dd>
                    </div>
                    {sector && (
                      <div className="sm:col-span-1">
                        <dt className="text-sm font-medium text-gray-500">Secteur</dt>
                        <dd className="mt-1 text-sm text-gray-900">{sector}</dd>
                      </div>
                    )}
                    {occupation && (
                      <div className="sm:col-span-1">
                        <dt className="text-sm font-medium text-gray-500">Métiers</dt>
                        <dd className="mt-1 text-sm text-gray-900">{occupation}</dd>
                      </div>
                    )}
                    {client && (
                      <div className="sm:col-span-1">
                        <dt className="text-sm font-medium text-gray-500">Client</dt>
                        <dd className="mt-1 text-sm text-gray-900">{client}</dd>
                      </div>
                    )}
                    <div className="sm:col-span-1">
                      <dt className="text-sm font-medium text-gray-500">Expérience</dt>
                      <dd className="mt-1 text-sm text-gray-900">{experiences[experience] || experience}</dd>
                    </div>
                    {/* <div className="sm:col-span-1">
                      <dt className="text-sm font-medium text-gray-500">Salaires traités</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {formatCurrency({ locale: router.locale, value: +companySalaryTreated?.min, compact: true })} à{' '}
                        {formatCurrency({ locale: router.locale, value: +companySalaryTreated?.max, compact: true })}
                      </dd>
                    </div> */}

                    {context && (
                      <div className="sm:col-span-2">
                        <details className="group" {...(openDescription ? { open: true } : {})}>
                          <summary className="list-none">
                            <dt className="font-medium text-sm text-gray-500 flex items-center cursor-pointer focus-visible:outline-none focus:ring focus:ring-indigo-200 gap-3">
                              <span>Le contexte qui occasionne ce partage</span>
                              <div className="border-4 border-transparent border-l-gray-600 mr-2 group-open:rotate-90 transition-transform origin-left"></div>
                            </dt>
                          </summary>
                          <dd className="text-sm mt-1 text-gray-900 whitespace-pre-line border border-gray-100 p-2 bg-gray-50">{context}</dd>
                        </details>
                      </div>
                    )}
                    {description && (
                      <div className="sm:col-span-2">
                        <details className="group" {...(openDescription ? { open: true } : {})}>
                          <summary className="list-none">
                            <dt className="font-medium text-sm text-gray-500 flex items-center cursor-pointer focus-visible:outline-none focus:ring focus:ring-indigo-200 gap-3">
                              <span>Questions de prévalidation pour la mission</span>
                              <div className="border-4 border-transparent border-l-gray-600 mr-2 group-open:rotate-90 transition-transform origin-left"></div>
                            </dt>
                          </summary>
                          <dd className="text-sm mt-1 text-gray-900 whitespace-pre-line border border-gray-100 p-2 bg-gray-50">{description}</dd>
                        </details>
                      </div>
                    )}
                  </dl>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>
    </>
  );
};

export default AvailableProfileDetails;
